// The historical dock as a DONUT (Chris, 2026-10-24: "what about a donut chart for a historical dock view option?"). One ring, each category a slice as wide as its share of everyone, the busiest first (or last), a key beside
// it with each name and share, and the total in the middle. Pure (no DOM, no d3): clusterService.ts feeds it the dock's categories and historicalDockDraw.ts draws what comes back.
//
// Slices are drawn as dashes along one circle (a stroke with a dash length and an offset), so a slice that grows glides there with a plain CSS transition. EVERY group gets its slice and its row in the key for as
// long as the key has room for the row (as many columns as the dock is wide enough for); only when there are more groups than that are the smallest folded into one grey "Other" slice, and the key says how many:
// a donut that silently dropped groups would be a wrong picture.
import { HistoricalRowsOrder, RowsBox, sortForRows } from "./historicalRows";

export interface DonutItem {
  id: string;
  value: number;
}

/** the most keys ever shown in a column of a very small dock: the ring and key never go below this many rows */
const MIN_KEY_ROWS = 4;
export const DONUT_OTHER_ID = "\u0000other";
const MIN_R = 38;
const MAX_R = 130;
const RING_SHARE = 0.34;   // the ring is this share of the radius (the hole is the rest)
const KEY_ROW_H = 20;
const KEY_W = 210;
const GAP_BETWEEN = 32;
const MARGIN = 14;
const LEAD_ROW_H = 17;         // one label's height down a side
const LEAD_ELBOW = 14;         // how far a line runs straight out from the ring before it bends
const LEAD_GAP_X = 46;         // from the ring's edge to the edge of the labels
const LEAD_LABEL_ROOM = 108;   // the width a name and its share need beside the line
const LEAD_MIN_R = 56;         // the smallest ring that is still worth putting names around
const LEAD_MIN_H = 130;        // a dock shorter than this keeps the key beside the ring: there is no room for labels down both sides

export interface DonutSlice {
  id: string;
  index: number;
  /** where it starts and how long it is, as fractions of the whole ring (0..1), going clockwise from the top */
  start: number;
  len: number;
  share: number;
  value: number;
  /** the middle of the slice, on the ring: where a leaving visitor flies to */
  tipX: number;
  tipY: number;
}

export interface DonutKeyRow {
  id: string;
  x: number;
  y: number;
}

/** a label on a line: where the line leaves the ring, where it bends, and where the label's edge is (its text runs away from the ring from there) */
export interface DonutLead {
  id: string;
  side: "left" | "right";
  ax: number;
  ay: number;
  bx: number;
  by: number;
  /** the end of the line, and the label's edge */
  lx: number;
  ly: number;
  /** how wide the label may be before it reaches the edge of the dock */
  room: number;
}

export interface DonutLayout {
  cx: number;
  cy: number;
  /** outer radius, inner radius, and the radius the dashes are drawn on (the middle of the ring) */
  R: number;
  r: number;
  rMid: number;
  total: number;
  slices: Map<string, DonutSlice>;
  /** the key beside the ring: one row per slice, in the ring's order, "Other" last */
  keyRows: DonutKeyRow[];
  keyWidth: number;
  /** categories folded into "Other", and what they add up to */
  otherCount: number;
  /** names and shares on lines around the ring, when they were asked for and the dock is tall enough; null = the key beside the ring is used */
  leads: DonutLead[] | null;
}

export const layoutDonut = (items: DonutItem[], box: RowsBox, order: HistoricalRowsOrder, leaders = false): DonutLayout => {
  const H = Math.max(0, box.bottom - box.top - 2 * MARGIN);
  const W = Math.max(0, box.right - box.left - 2 * MARGIN);
  // names on lines need room each side of the ring: the ring gives up size for it (down to LEAD_MIN_R); if even that is not enough the key beside the ring is used
  const ringR = Math.max(MIN_R, Math.min(MAX_R, H / 2));
  const leadR = Math.min(ringR, W / 2 - LEAD_GAP_X - LEAD_LABEL_ROOM);
  const useLeads = leaders && H >= LEAD_MIN_H && leadR >= LEAD_MIN_R;
  const R = useLeads ? leadR : ringR;
  const r = R * (1 - RING_SHARE);
  const rMid = (R + r) / 2;
  const present = items.filter((i) => i.value > 0);
  const biggest = sortForRows(present.map((i) => ({ id: i.id, value: i.value, size: 0 })), "desc");
  const total = present.reduce((n, i) => n + i.value, 0);
  // how many keys the dock has room for: rows down a column (the ring's height) times the columns across (what is left of the width beside the ring)
  const rowsFit = Math.max(MIN_KEY_ROWS, Math.floor(H / KEY_ROW_H));
  const maxCols = Math.max(1, Math.floor((W - 2 * R - GAP_BETWEEN) / KEY_W));
  const capacity = useLeads ? 2 * leadRowsFit(H) : rowsFit * maxCols;
  const all = biggest.length <= capacity;
  const kept = all ? biggest : biggest.slice(0, capacity - 1);   // (one place is kept for "Other")
  const rest = all ? [] : biggest.slice(capacity - 1);
  const otherValue = rest.reduce((n, i) => n + i.value, 0);
  const ordered = sortForRows(kept, order).map((i) => ({ id: i.id, value: i.value }));
  if (otherValue > 0) ordered.push({ id: DONUT_OTHER_ID, value: otherValue });
  // the block (ring + key) is centred across the box
  const keyCols = Math.max(1, Math.ceil(ordered.length / rowsFit));
  const keyWidth = keyCols * KEY_W;
  const blockW = 2 * R + GAP_BETWEEN + keyWidth;
  const left = box.left + MARGIN + Math.max(0, (W - blockW) / 2);
  const cx = useLeads ? box.left + MARGIN + W / 2 : left + R;
  const cy = box.top + MARGIN + H / 2;
  const slices = new Map<string, DonutSlice>();
  let at = 0;
  ordered.forEach((item, index) => {
    const share = total > 0 ? item.value / total : 0;
    const mid = (at + share / 2) * 2 * Math.PI - Math.PI / 2;
    slices.set(item.id, { id: item.id, index, start: at, len: share, share, value: item.value, tipX: cx + rMid * Math.cos(mid), tipY: cy + rMid * Math.sin(mid) });
    at += share;
  });
  const keyLeft = cx + R + GAP_BETWEEN;
  const keyTop = cy - (Math.min(ordered.length, rowsFit) * KEY_ROW_H) / 2;
  const keyRows = useLeads ? [] : ordered.map((item, index) => ({ id: item.id, x: keyLeft + Math.floor(index / rowsFit) * KEY_W, y: keyTop + (index % rowsFit) * KEY_ROW_H + KEY_ROW_H / 2 }));
  const leads = useLeads ? leadLines(Array.from(slices.values()), cx, cy, R, box) : null;

  return { cx, cy, R, r, rMid, total, slices, keyRows, keyWidth: useLeads ? 0 : keyWidth, otherCount: rest.length, leads };
};

/**
 * The lines: each slice's line leaves the ring at the middle of the slice, runs straight out a little, then goes to a label on the left or right of the ring (a slice on the right half has its label on the
 * right). Down each side the labels are spaced a row apart, as close to their slice's own height as they can be: they are pushed down where they would touch, and the whole stack is pushed back up where it would
 * run off the bottom, so no two labels ever overlap and none leaves the dock.
 */
const leadRowsFit = (H: number): number => Math.max(1, Math.floor((H - 2 * MARGIN) / LEAD_ROW_H));

const leadLines = (slices: DonutSlice[], cx: number, cy: number, R: number, box: RowsBox): DonutLead[] => {
  const top = box.top + MARGIN + LEAD_ROW_H / 2;
  const bottom = box.bottom - MARGIN - LEAD_ROW_H / 2;
  const leads: DonutLead[] = [];
  const cap = leadRowsFit(box.bottom - box.top);
  const all = slices
    .map((s) => {
      const angle = (s.start + s.len / 2) * 2 * Math.PI;            // clockwise from the top
      return { s, angle, x: Math.sin(angle), y: -Math.cos(angle) };
    })
    .sort((a, b) => a.angle - b.angle);
  // the right half is the first part of the ring and the left half the rest; when one half has more names than a side has rows, the ones nearest the bottom go over to the other side
  let split = all.filter((m) => m.x >= 0).length;
  split = Math.max(all.length - cap, Math.min(cap, split));
  const sideOf = new Map(all.map((m, i) => [m.s.id, i < split ? "right" : "left"] as const));
  (["right", "left"] as const).forEach((side) => {
    const mine = all
      .filter((m) => sideOf.get(m.s.id) === side)
      .sort((a, b) => a.y - b.y || a.s.index - b.s.index);
    const ys = mine.map((m) => Math.max(top, Math.min(bottom, cy + m.y * (R + LEAD_ELBOW))));
    for (let i = 1; i < ys.length; i++) ys[i] = Math.max(ys[i], ys[i - 1] + LEAD_ROW_H);
    if (ys.length && ys[ys.length - 1] > bottom) {
      ys[ys.length - 1] = bottom;
      for (let i = ys.length - 2; i >= 0; i--) ys[i] = Math.min(ys[i], ys[i + 1] - LEAD_ROW_H);
    }
    const edge = side === "right" ? cx + R + LEAD_GAP_X : cx - R - LEAD_GAP_X;
    mine.forEach((m, i) => {
      leads.push({
        id: m.s.id,
        side,
        ax: cx + m.x * (R + 1),
        ay: cy + m.y * (R + 1),
        bx: cx + m.x * (R + LEAD_ELBOW),
        by: cy + m.y * (R + LEAD_ELBOW),
        lx: edge + (side === "right" ? -8 : 8),
        ly: ys[i],
        room: Math.max(40, (box.right - box.left - 2 * MARGIN) / 2 - (R + LEAD_GAP_X - 8) - 6),
      });
    });
  });

  return leads;
};
